/**
 * Which matter types a firm may work in, and therefore which ones every surface
 * that offers a choice is allowed to show.
 *
 * **This is a visibility rule, not a capability one** — deliberately separate
 * from `matter-type-capabilities.ts`, which mirrors the backend's published
 * contract for what a matter of a given type can *hold*. This file answers a
 * different question ("may this firm open one at all?"), has no backend mirror,
 * and is enforced on the frontend only: `GET /matter-types` serves every active
 * type to every firm.
 *
 * **`matter-types` means the non-estate practice areas** (VALE-1705). Estate
 * administration and estate planning are the wills-and-estates practice Vale is
 * sold into, so every firm gets both with no flag at all; conveyancing,
 * litigation, family law and the rest stay behind the flag. Estate dispute and
 * estate advice stay behind it too — that was the explicit scope call, not an
 * oversight.
 *
 * Before this existed the answer was restated inline at each of eleven call
 * sites as a comparison against estate administration, which is why the set
 * could drift and why adding a second always-available type meant finding all
 * eleven. Ask this module instead.
 */

import { MatterTypeSlug } from '@/lib/services/matter-types/types';

/**
 * The matter types available to every firm, flag or no flag.
 *
 * Adding a member here is the single change that opens a type to the whole
 * customer base. Everything else in this file is derived from it, including the
 * display rule below — so a set that ever returns to one member takes the type
 * indicators back down with it automatically.
 */
const ALWAYS_SELECTABLE_MATTER_TYPES: ReadonlySet<string> = new Set([
  MatterTypeSlug.ESTATE_ADMINISTRATION,
  MatterTypeSlug.ESTATE_PLANNING
]);

/** Whether a firm in this flag state may open a matter, enquiry or intake of this type. */
export function isMatterTypeSelectable(
  slug: string | null | undefined,
  hasMatterTypesFeature: boolean
): boolean {
  if (hasMatterTypesFeature) return true;
  return ALWAYS_SELECTABLE_MATTER_TYPES.has(slug?.toLowerCase() ?? '');
}

/**
 * Narrow a served matter-type list to what this firm may actually choose.
 *
 * Generic over the row shape because the surfaces that call it hold different
 * ones (`MatterType` from the API, the intake form-type rows), and all of them
 * key on `slug`.
 */
export function selectableMatterTypes<T extends { slug: string }>(
  types: readonly T[] | undefined | null,
  hasMatterTypesFeature: boolean
): T[] {
  return (types ?? []).filter((type) =>
    isMatterTypeSelectable(type.slug, hasMatterTypesFeature)
  );
}

/**
 * Whether a type indicator (a badge, a per-type glyph, a header label) carries
 * information for this firm.
 *
 * **Derived, never asserted.** Three display surfaces used to hide their
 * indicator whenever `matter-types` was off, on the reasoning that every matter
 * was then an estate administration and there was nothing to disambiguate. That
 * reasoning was sound and is now false: the un-flagged set has two members, so
 * a firm with no flags sees both administration and planning matters side by
 * side and the indicator is exactly what tells them apart. Keying the rule on
 * the set's size rather than restating `true` is what keeps the two in step.
 */
export function matterTypeIndicatorsAreMeaningful(
  hasMatterTypesFeature: boolean
): boolean {
  return hasMatterTypesFeature || ALWAYS_SELECTABLE_MATTER_TYPES.size > 1;
}

/** The registry set, exported **only** for tests that pin the membership. */
export const ALWAYS_SELECTABLE_MATTER_TYPE_SLUGS =
  ALWAYS_SELECTABLE_MATTER_TYPES;
